import { Layer, TableToolbar, TableToolbarContent, TableToolbarSearch } from "@carbon/react";
import {
  MultiSelectAppliedFilter,
  MultiSelectAppliedFilterSkeleton,
  SingleListAppliedFilterSkeleton,
  SingleSelectAppliedFilter,
} from "@flaminem/ui-components";
import React, { ChangeEvent, useEffect, useState } from "react";

import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import { useGetCaseTypesSchemasQuery } from "src/graphql/anti-fraud/queries/caseTypes/CaseTypesSchemasQuery";
import { useGetRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { useAlertValidationDecisions } from "src/hooks/alerts/useAlertValidationDecisions";
import { debounceSearchTime } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { AlertsFilter } from "src/pages/Alerts/AlertsFilter.tsx";
import { AlertValidationDecision } from "src/types/alert";
import { debounce } from "src/utils/functions.utils.ts";

type AlertsToolbarProps = {
  searchTerm: string;
  handleOnSearchTermChange: (value: string) => void;
  ruleIds: string[];
  caseTypeCodes: string[];
  onlyUserAlerts: boolean;
  decisions: AlertValidationDecision[];
  displayDecisionFilter: boolean;
  placeholder?: string;
  handleOnFiltersChange: (filters: {
    ruleIds: string[];
    onlyUserAlerts: boolean;
    decisions: AlertValidationDecision[];
    caseTypeCodes: string[];
  }) => void;
};

const AlertsToolbar: React.FC<AlertsToolbarProps> = ({
  searchTerm,
  handleOnSearchTermChange,
  ruleIds,
  handleOnFiltersChange,
  onlyUserAlerts,
  displayDecisionFilter,
  decisions,
  caseTypeCodes,
  placeholder,
}) => {
  const { data: rules, loading: rulesLoading, error: rulesError } = useGetRulesQuery();
  const selectedRuleItems = rules.filter((rule) => ruleIds.includes(rule.id));

  const {
    data: caseTypes,
    loading: caseTypesLoading,
    error: caseTypesError,
  } = useGetCaseTypesSchemasQuery();

  const selectedCaseTypeItems = caseTypes?.filter((caseType) =>
    caseTypeCodes.includes(caseType.code)
  );

  const decisionsData = useAlertValidationDecisions();
  const selectedDecisionItems = decisionsData.filter((decision) => decisions.includes(decision.id));

  const t = useT();
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = React.useMemo(() => {
    return debounce(handleOnSearchTermChange, debounceSearchTime);
    // We don't want to re-create the debounced function on every render, it makes unnecessary calls to the API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearchTerm = (value: string) => {
    setCurrentSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchInputChange = (event: "" | ChangeEvent<HTMLInputElement>) => {
    if (!event) return;
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent className="border-b border-solid border-borderSubtle01">
          <TableToolbarSearch
            placeholder={placeholder || t("page.alerts.table.search.placeholder")}
            onChange={handleSearchInputChange}
            onClear={() => setSearchTerm("")}
            persistent={true}
            value={currentSearchTerm}
          />
          <AlertsFilter
            displayDecisionFilter={displayDecisionFilter}
            values={{ ruleIds, onlyUserAlerts, decisions, caseTypeCodes }}
            onApplyFilter={handleOnFiltersChange}
            onResetFilter={() =>
              handleOnFiltersChange({
                ruleIds: [],
                onlyUserAlerts: false,
                decisions: [],
                caseTypeCodes: [],
              })
            }
            caseTypes={caseTypes}
            rules={rules}
            loading={rulesLoading || caseTypesLoading}
            error={rulesError || caseTypesError}
          />
        </TableToolbarContent>
      </TableToolbar>
      {((selectedRuleItems && selectedRuleItems.length > 0) ||
        (selectedDecisionItems && selectedDecisionItems.length > 0) ||
        onlyUserAlerts ||
        (selectedCaseTypeItems && selectedCaseTypeItems.length > 0) ||
        rulesLoading ||
        caseTypesLoading ||
        decisions.length > 0) && (
        <div className="flex w-full bg-layer">
          <Layer className={"p-5"}>
            <AppliedFilters
              onClear={() =>
                handleOnFiltersChange({
                  ruleIds: [],
                  onlyUserAlerts: false,
                  decisions: [],
                  caseTypeCodes: [],
                })
              }
            >
              {rulesLoading || caseTypesLoading ? (
                <>
                  <SingleListAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.applied.only.user.filter.title")}
                  />
                  <MultiSelectAppliedFilterSkeleton title={t("rules.title")} />
                  <MultiSelectAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.form.decisions.title")}
                  />
                  <MultiSelectAppliedFilterSkeleton
                    title={t("page.alerts.table.filters.form.case.type.title")}
                  />
                </>
              ) : (
                <>
                  {onlyUserAlerts && (
                    <SingleSelectAppliedFilter
                      item={{
                        label: t("page.alerts.table.filters.applied.only.user.filter.value"),
                        value: t("page.alerts.table.filters.applied.only.user.filter.value"),
                      }}
                      title={t("page.alerts.table.filters.applied.only.user.filter.title")}
                      onClear={() =>
                        handleOnFiltersChange({
                          ruleIds: ruleIds,
                          onlyUserAlerts: false,
                          decisions,
                          caseTypeCodes: caseTypeCodes,
                        })
                      }
                    />
                  )}
                  {selectedRuleItems && selectedRuleItems.length > 0 && (
                    <MultiSelectAppliedFilter
                      items={selectedRuleItems.map((item) => ({
                        label: item.name,
                        value: item.id,
                      }))}
                      title={t("rules.title")}
                      onChange={(values) =>
                        handleOnFiltersChange({
                          ruleIds: values,
                          onlyUserAlerts: onlyUserAlerts,
                          decisions,
                          caseTypeCodes: caseTypeCodes,
                        })
                      }
                    />
                  )}
                </>
              )}
              {selectedCaseTypeItems && selectedCaseTypeItems.length > 0 && (
                <MultiSelectAppliedFilter
                  items={selectedCaseTypeItems.map((item) => ({
                    label: item.name,
                    value: item.code,
                  }))}
                  title={t("page.alerts.table.filters.case.type.filter.title")}
                  onChange={(values) =>
                    handleOnFiltersChange({
                      ruleIds: ruleIds,
                      onlyUserAlerts: onlyUserAlerts,
                      decisions,
                      caseTypeCodes: values,
                    })
                  }
                />
              )}
              {decisions.length > 0 && (
                <MultiSelectAppliedFilter
                  items={selectedDecisionItems.map((item) => ({
                    label: item.title,
                    value: item.id,
                  }))}
                  title={t("page.alerts.table.filters.form.decisions.title")}
                  onChange={(values) =>
                    handleOnFiltersChange({
                      ruleIds: ruleIds,
                      onlyUserAlerts: onlyUserAlerts,
                      decisions: values as AlertValidationDecision[],
                      caseTypeCodes: caseTypeCodes,
                    })
                  }
                />
              )}
            </AppliedFilters>
          </Layer>
        </div>
      )}
    </>
  );
};

export default AlertsToolbar;
